import React, {
  createContext,
  useEffect,
  useReducer,
  Dispatch,
  useCallback,
  useState,
} from 'react';
import { mutate } from 'swr';
import {
  useInfiniteThreads,
  useUser,
  useRooms,
  useTasks,
  useMessages,
  getThreadsUrl,
  apiRequest,
  useThreads,
  getSubstringIndex,
} from '@groupthinkai/groupthink';

import { ChatState, reducer, initialChatState } from './reducer';
import {
  ChatAction,
  setThreadsLoadingAction,
  setThreadsDataAction,
  sendMessageOptimisticAction,
  sendMessageConfirmedAction,
  sendMessageRevertAction,
  addReactionOptimisticAction,
  addReactionConfirmedAction,
  addReactionRevertAction,
  removeReactionOptimisticAction,
  removeReactionConfirmedAction,
  removeReactionRevertAction,
  deleteMessageAction,
  editMessageAction,
  handleMessageSentEventAction,
  handleMessageReactedToEventAction,
  setMessagesDataAction,
  setMessagesErrorAction,
  setMessagesLoadingAction,
  setThreadsErrorAction,
  setThreadablesValidatingAction,
  setThreadablesLoadingAction,
  setThreadablesDataAction,
  setThreadablesErrorAction,
  deleteThreadAction,
  deleteThreadableAction,
  markThreadAsReadAction,
  setThreadsValidatingAction,
  setMessagesValidatingAction,
} from './actions';

export interface Attachment {
  url: string;
  file: File;
  preview: string;
}

/**
 * Filters suggested users based on a search query
 * Searches through display name, username, and email
 */
export const getSuggestedUsersForQuery = (suggestedUsers: any[], query: string): any[] => {
  const ignoreAccents = true;
  const results = [];

  for (let i = 0, l = suggestedUsers.length; i < l; ++i) {
    const suggestedUser = suggestedUsers[i];
    const display = suggestedUser.display || `${suggestedUser.id}`;
    const username = suggestedUser.username;
    const email = suggestedUser.email;

    if (getSubstringIndex(display, query, ignoreAccents) >= 0) {
      results.push(suggestedUser);
    } else if (username && getSubstringIndex(username, query, ignoreAccents) >= 0) {
      results.push(suggestedUser);
    } else if (email && getSubstringIndex(email, query, ignoreAccents) >= 0) {
      results.push(suggestedUser);
    }
  }

  return results;
};

const selectRooms = (state: ChatState): Groupthink.RoomResource[] =>
  state.threadables.byType['room'].allIds.map((id) => state.threadables.byType['room'].byId[id]);

const selectRoomsLoading = (state: ChatState): boolean =>
  state.threadables.byType['room'].loading &&
  !state.threadables.byType['room'].byId &&
  !state.threadables.byType['room'].allIds;

const selectRoomsValidating = (state: ChatState): boolean =>
  state.threadables.byType['room'].validating;

const selectTasks = (state: ChatState): Groupthink.TaskResource[] =>
  state.threadables.byType['task'].allIds.map((id) => state.threadables.byType['task'].byId[id]);

const selectTasksLoading = (state: ChatState): boolean =>
  state.threadables.byType['task'].loading &&
  !state.threadables.byType['task'].byId &&
  !state.threadables.byType['task'].allIds;

const selectTasksValidating = (state: ChatState): boolean =>
  state.threadables.byType['task'].validating;

const selectThreads = (state: ChatState, threadableId: string): Groupthink.ThreadResource[] => {
  if (!threadableId) return [];

  const threadableThreads = state.threads.byThreadableId[threadableId];
  if (!threadableThreads) return [];

  return threadableThreads.allIds.map((id) => threadableThreads.byId[id]);
};

const selectThreadsLoading = (state: ChatState, threadableId: string): boolean => {
  if (!threadableId) return false;

  const roomThreads = state.threads.byThreadableId[threadableId];

  return roomThreads ? roomThreads.loading && !roomThreads.byId && !roomThreads.allIds : false;
};

const selectThreadsValidating = (state: ChatState, threadableId: string): boolean => {
  if (!threadableId) return false;

  const roomThreads = state.threads.byThreadableId[threadableId];
  return roomThreads ? roomThreads.validating : false;
};

const selectHasMorePages = (state: ChatState, threadableId: string): boolean => {
  if (!threadableId) return false;

  const roomThreads = state.threads.byThreadableId[threadableId];
  return roomThreads ? roomThreads.hasMorePages : false;
};

const selectMessages = (state: ChatState, threadId: string): Groupthink.MessageResource[] => {
  if (!threadId) return [];

  const threadMessages = state.messages.byThreadId[threadId];
  if (!threadMessages) return [];

  return threadMessages.allIds.map((id) => threadMessages.byId[id]);
};

const selectMessagesLoading = (state: ChatState, threadId: string): boolean => {
  if (!threadId) return false;

  const threadMessages = state.messages.byThreadId[threadId];

  return threadMessages
    ? threadMessages.loading && !threadMessages.byId && !threadMessages.allIds
    : false;
};

const selectMessagesValidating = (state: ChatState, threadId: string): boolean => {
  if (!threadId) return false;

  const threadMessages = state.messages.byThreadId[threadId];

  return threadMessages ? threadMessages.loading : false;
};

const ChatContext = createContext<
  | {
      state: ChatState;
      dispatch: Dispatch<ChatAction>;

      mutateRooms: () => void;
      mutateTasks: () => void;
      mutateThreads: () => void;
      mutateMessages: () => void;

      currentThreadableId: string | null;
      currentThreadId: string | null;

      /**
       * Load data for a threadable.
       * Specifically, sets the active threadable id and fetches threads for that threadable.
       */
      setCurrentThreadable: (threadableId: string) => void;

      /**
       * Load data for a thread.
       * Specifically, sets the active Thread ID and fetches messages for that thread.
       */
      setCurrentThread: (threadableId: string, threadId: string) => void;

      /**
       * Fetch additional threads for the current room, as in the case of infinite scrolling.
       */
      fetchMoreThreads: () => void;

      deleteThreadable: (threadableType: string, threadableId: string) => void;
      leaveThreadable: (threadableType: string, threadableId: string) => void;
      deleteThread: (threadId: string) => void;

      markThreadAsRead: (threadableId: string, threadId: string, messageId: string) => void;

      sendMessage: (
        threadId: string,
        message: {
          content: string;
          attachments: Attachment[];
          onSuccess?: (message: Groupthink.MessageResource) => void;
          onError?: () => void;
        }
      ) => void;
      deleteMessage: (threadId: string, messageId: string) => void;
      editMessage: (threadId: string, messageId: string, content: string) => void;
      addReaction: (threadId: string, messageId: string, emoji: string) => void;
      removeReaction: (threadId: string, messageId: string, reactionId: string) => void;

      /**
       * Selectors for the state.
       */
      selectors: {
        selectRooms: (state: ChatState) => Groupthink.RoomResource[];
        selectRoomsLoading: (state: ChatState) => boolean;
        selectRoomsValidating: (state: ChatState) => boolean;
        selectThreads: (state: ChatState, threadableId: string) => Groupthink.ThreadResource[];
        selectThreadsLoading: (state: ChatState, threadableId: string) => boolean;
        selectThreadsValidating: (state: ChatState, threadableId: string) => boolean;
        selectHasMorePages: (state: ChatState, threadableId: string) => boolean;
        selectMessages: (state: ChatState, threadId: string) => Groupthink.MessageResource[];
        selectMessagesLoading: (state: ChatState, threadId: string) => boolean;
        selectMessagesValidating: (state: ChatState, threadId: string) => boolean;
      };
    }
  | undefined
>(undefined);

type ChatProviderProps = {
  workspaceId: string;
  usePusher?: Groupthink.RealtimePusherHandler;
  children: React.ReactNode;
};

export const ChatProvider = ({ workspaceId, usePusher, children }: ChatProviderProps) => {
  const [state, dispatch] = useReducer<React.Reducer<ChatState, ChatAction>>(
    reducer,
    initialChatState
  );

  const [currentThreadableId, setCurrentThreadableId] = useState<string | null>(null);
  const [currentThreadId, setCurrentThreadId] = useState<string | null>(null);

  const stableWorkspaceId = React.useMemo(() => workspaceId, [workspaceId]);

  /**
   * ----------------------------------------
   * Sync State with Data, Loading, and Error
   * ----------------------------------------
   */

  /**
   * Current User:
   * - Fetch Data
   */
  const { user } = useUser('me');

  /**
   * Rooms:
   * - Fetch Data
   */
  const {
    rooms,
    isError: roomsError,
    mutate: mutateRooms,
    isLoading: roomsLoading,
    isValidating: roomsValidating,
  } = useRooms(stableWorkspaceId);

  /**
   * Rooms:
   * - Sync Data State
   */
  useEffect(() => {
    dispatch(setThreadablesLoadingAction('room', roomsLoading));
  }, [roomsLoading]);
  useEffect(() => {
    dispatch(setThreadablesValidatingAction('room', roomsValidating));
  }, [roomsValidating]);
  useEffect(() => {
    if (rooms) {
      const filteredRooms = rooms.filter((room) => room.workspace_id === stableWorkspaceId);
      dispatch(setThreadablesDataAction('room', filteredRooms));
    }
  }, [rooms]);
  useEffect(() => {
    if (roomsError) {
      dispatch(setThreadablesErrorAction('room', roomsError));
    }
  }, [roomsError?.message]);

  /**
   * Tasks:
   * - Fetch Data
   */
  const {
    tasks,
    isError: tasksError,
    mutate: mutateTasks,
    isLoading: tasksLoading,
    isValidating: tasksValidating,
  } = useTasks(stableWorkspaceId);

  /**
   * Tasks:
   * - Sync Data State
   */
  useEffect(() => {
    dispatch(setThreadablesLoadingAction('task', tasksLoading));
  }, [tasksLoading]);
  useEffect(() => {
    dispatch(setThreadablesValidatingAction('task', tasksValidating));
  }, [tasksValidating]);
  useEffect(() => {
    if (tasks) {
      const filteredTasks = tasks.filter((task) => task.workspace_id === stableWorkspaceId);
      dispatch(setThreadablesDataAction('task', filteredTasks));
    }
  }, [tasks]);
  useEffect(() => {
    if (tasksError) {
      dispatch(setThreadablesErrorAction('task', tasksError));
    }
  }, [tasksError?.message]);

  /**
   * Threads:
   * - Fetch Data
   */
  const {
    threads,
    error: threadsError,
    size,
    setSize,
    hasMorePages,
    isLoading: threadsLoading,
    isValidating: threadsValidating,
    mutate: mutateThreads,
    threadableId: threadsThreadableId, // Echo the threadableId used for the request - helps keep state in sync
  } = useInfiniteThreads(currentThreadableId);
  const { markThreadAsRead: markThreadAsReadHook } = useThreads(currentThreadableId);

  /**
   * Threads:
   * - Sync Data State
   */
  useEffect(() => {
    dispatch(setThreadsLoadingAction(threadsThreadableId, threadsLoading));
  }, [threadsThreadableId, threadsLoading]);
  useEffect(() => {
    dispatch(setThreadsValidatingAction(threadsThreadableId, threadsValidating));
  }, [threadsThreadableId, threadsValidating]);
  useEffect(() => {
    if (threads) {
      // Ensure the data we're setting is for the correct threadableId
      const filteredThreads = threads.filter(
        (thread) => thread.threadable_id === threadsThreadableId
      );

      dispatch(setThreadsDataAction(threadsThreadableId, filteredThreads, hasMorePages));
    }
  }, [threadsThreadableId, threads, hasMorePages]);
  useEffect(() => {
    if (threadsError) {
      dispatch(setThreadsErrorAction(threadsThreadableId, threadsError));
    }
  }, [threadsThreadableId, threadsError?.message]);

  /**
   * Messages:
   * - Fetch Data
   */
  const {
    messages,
    isError: messagesError,
    isLoading: messagesLoading,
    isValidating: messagesValidating,
    mutate: mutateMessages,
    threadId: messagesThreadId,
    threadableId: messagesThreadableId,
  } = useMessages(currentThreadableId, currentThreadId);

  /**
   * Messages:
   * - Sync Data State
   */
  useEffect(() => {
    if (currentThreadId) {
      dispatch(setMessagesLoadingAction(currentThreadId, messagesLoading));
    }
  }, [currentThreadId, messagesLoading]);
  useEffect(() => {
    if (currentThreadId) {
      dispatch(setMessagesValidatingAction(currentThreadId, messagesValidating));
    }
  }, [currentThreadId, messagesValidating]);
  useEffect(() => {
    if (messagesThreadId && messages) {
      // Ensure the data we're setting is for the correct threadableId
      const filteredMessages = messages.filter(
        (message) =>
          message.thread_id === messagesThreadId && message.threadable_id === messagesThreadableId
      );

      dispatch(setMessagesDataAction(messagesThreadId, filteredMessages));
    }
  }, [messagesThreadId, messages]);
  useEffect(() => {
    if (messagesThreadId && messagesError) {
      dispatch(setMessagesErrorAction(messagesThreadId, messagesError));
    }
  }, [messagesError?.message]);

  /**
   * ----------------------------------------
   * Realtime Events
   * ----------------------------------------
   */
  const MESSAGE_SENT_EVENT_NAME = 'MessageSent';
  const MESSAGE_REACTED_TO_EVENT_NAME = 'MessageReactedTo';

  const CURRENT_USER_CHANNEL_NAME = React.useMemo(() => (user ? `users.${user.id}` : null), [user]);

  const CURRENT_ROOM_CHANNEL_NAME = React.useMemo(
    () =>
      currentThreadableId && currentThreadableId.startsWith('RM')
        ? `rooms.${currentThreadableId}.messages`
        : null,
    [currentThreadableId]
  );
  const CURRENT_TASK_CHANNEL_NAME = React.useMemo(
    () =>
      currentThreadableId && currentThreadableId.startsWith('TK')
        ? `tasks.${currentThreadableId}.messages`
        : null,
    [currentThreadableId]
  );

  const messageSentUserChannelEventCallback = useCallback(() => {
    mutateRooms();
    mutateTasks();
  }, [mutateRooms, mutateTasks]);
  usePusher?.({
    type: MESSAGE_SENT_EVENT_NAME,
    channel: CURRENT_USER_CHANNEL_NAME,
    callback: messageSentUserChannelEventCallback,
  });

  const messageSentRoomChannelEventCallback = useCallback(
    (payload: any) => {
      dispatch(handleMessageSentEventAction(payload, user));
    },
    [dispatch, handleMessageSentEventAction, user]
  );
  usePusher?.({
    type: MESSAGE_SENT_EVENT_NAME,
    channel: CURRENT_ROOM_CHANNEL_NAME,
    callback: messageSentRoomChannelEventCallback,
  });

  const messageSentTaskChannelEventCallback = useCallback(
    (payload: any) => {
      dispatch(handleMessageSentEventAction(payload, user));
    },
    [dispatch, handleMessageSentEventAction, user]
  );
  usePusher?.({
    type: MESSAGE_SENT_EVENT_NAME,
    channel: CURRENT_TASK_CHANNEL_NAME,
    callback: messageSentTaskChannelEventCallback,
  });

  const messageReactedToRoomChannelEventCallback = useCallback(
    (payload: any) => {
      dispatch(handleMessageReactedToEventAction(payload));
    },
    [dispatch, handleMessageReactedToEventAction]
  );
  usePusher?.({
    type: MESSAGE_REACTED_TO_EVENT_NAME,
    channel: CURRENT_ROOM_CHANNEL_NAME,
    callback: messageReactedToRoomChannelEventCallback,
  });

  const messageReactedToTaskChannelEventCallback = useCallback(
    (payload: any) => {
      dispatch(handleMessageReactedToEventAction(payload));
    },
    [dispatch, handleMessageReactedToEventAction]
  );
  usePusher?.({
    type: MESSAGE_REACTED_TO_EVENT_NAME,
    channel: CURRENT_TASK_CHANNEL_NAME,
    callback: messageReactedToTaskChannelEventCallback,
  });

  /**
   * ----------------------------------------
   * Operations
   * ----------------------------------------
   */

  const setCurrentThreadable = useCallback(
    (threadableId: string) => {
      setCurrentThreadableId(threadableId);
    },
    [setCurrentThreadableId]
  );

  const setCurrentThread = useCallback(
    (threadableId: string, threadId: string) => {
      setCurrentThreadable(threadableId);
      setCurrentThreadId(threadId);
    },
    [setCurrentThreadable, setCurrentThreadId]
  );

  const fetchMoreThreads = useCallback(() => {
    // Increment the size to fetch more threads
    setSize(size + 1);
  }, [size, setSize]);

  // Message operations
  const sendMessage = useCallback(
    (
      threadId: string,
      message: {
        content: string;
        attachments: Attachment[];
        onSuccess?: (message: Groupthink.MessageResource) => void;
        onError?: () => void;
      }
    ) => {
      const { content, attachments = [], onSuccess, onError } = message;
      if (!threadId || (!content && attachments.length === 0)) {
        console.error('Cannot send message: missing threadId or content / files');
        return;
      }

      const threadableId = currentThreadableId;
      if (!threadableId) {
        console.error('Cannot send message: missing threadableId');
        return;
      }

      // Create a temporary ID for optimistic updates
      const tempId = `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

      // Get the current user for the optimistic update
      if (!user) {
        console.error('Cannot send message: user not authenticated');
        return;
      }

      // 1. Optimistically add the message to the UI
      dispatch(
        sendMessageOptimisticAction(threadableId, threadId, tempId, content, attachments, user)
      );

      // 2. Actually send the message to the API
      const messagesUrl = getThreadsUrl(threadableId) + `/${threadId}/messages`;
      const threadsUrl = getThreadsUrl(threadableId);

      apiRequest<'message.send'>(messagesUrl, mutate, 'POST', {
        setErrors: (errors) => {
          if (Object.keys(errors).length > 0) {
            console.error('Error sending message:', errors);
            dispatch(sendMessageRevertAction(threadableId, threadId, tempId));
            onError?.();
          }
        },
        payload: { content, attachments: attachments.map((att) => att.url).filter(Boolean) },
        onSuccess: (syncedMessage) => {
          // Handle success
          dispatch(
            sendMessageConfirmedAction(
              threadableId,
              threadId,
              tempId,
              syncedMessage as Groupthink.MessageResource
            )
          );
          mutate(messagesUrl);
          mutate(threadsUrl);
          onSuccess?.(syncedMessage as Groupthink.MessageResource);
        },
      });
    },
    [dispatch, user, workspaceId, currentThreadableId]
  );

  const deleteThreadable = useCallback(
    async (threadableType: string, threadableId: string) => {
      if (!threadableType) {
        console.error('Cannot delete threadable: missing threadableType');
        return;
      }

      if (!threadableId) {
        console.error('Cannot delete threadable: missing threadableId');
        return;
      }

      // API endpoint to delete the message
      const threadablesUrl = `/v1/${threadableType}s`;
      const threadableUrl = `${threadablesUrl}/${threadableId}`;

      // Make the API call to delete the thread
      apiRequest(threadableUrl, mutate, 'DELETE', {
        setErrors: (errors) => {
          if (Object.keys(errors).length > 0) {
            console.error('Error deleting threadable:', errors);
            // Note: No optimistic update to revert since we're not doing optimistic deletes
          }
        },
        onSuccess: () => {
          // If successful, update the reducer state
          dispatch(deleteThreadableAction(threadableType, threadableId));

          // Invalidate relevant caches
          mutate(threadablesUrl);

          // Also invalidate the specific cache hooks to refresh sidebar
          if (threadableType === 'room') {
            mutateRooms();
          }
        },
      });
    },
    [dispatch, mutateRooms]
  );

  const leaveThreadable = useCallback(
    async (threadableType: string, threadableId: string) => {
      if (!threadableType) {
        console.error('Cannot leave threadable: missing threadableType');
        return;
      }

      if (!threadableId) {
        console.error('Cannot leave threadable: missing threadableId');
        return;
      }

      if (threadableType !== 'room') {
        console.error('Leave threadable only supports rooms currently');
        return;
      }

      if (!user?.id) {
        console.error('Cannot leave room: no current user');
        return;
      }

      // API endpoint to remove user from room
      const leaveUrl = `/v1/workspaces/${workspaceId}/rooms/${threadableId}/users/${user.id}`;

      // Make the API call to remove current user from room
      apiRequest(leaveUrl, mutate, 'DELETE', {
        setErrors: (errors) => {
          if (Object.keys(errors).length > 0) {
            console.error('Error leaving room:', errors);
          }
        },
        onSuccess: () => {
          // Update the reducer state to remove the room
          dispatch(deleteThreadableAction(threadableType, threadableId));

          // Invalidate relevant caches
          const threadablesUrl = `/v1/${threadableType}s`;
          mutate(threadablesUrl);

          // Also invalidate the rooms cache to refresh sidebar
          mutateRooms();
        },
      });
    },
    [dispatch, mutateRooms, workspaceId, user?.id]
  );

  const deleteThread = useCallback(
    async (threadId: string) => {
      if (!threadId) {
        console.error('Cannot delete thread: missing threadId');
        return;
      }

      const threadableId = currentThreadableId;
      if (!threadableId) {
        console.error('Cannot delete thread: missing threadableId');
        return;
      }

      // API endpoint to delete the message
      const threadsUrl = getThreadsUrl(threadableId);
      const threadUrl = threadsUrl + `/${threadId}`;

      // Make the API call to delete the thread
      apiRequest<'thread.delete'>(threadUrl, mutate, 'DELETE', {
        setErrors: (errors) => {
          if (Object.keys(errors).length > 0) {
            console.error('Error deleting thread:', errors);
            // Note: No optimistic update to revert since we're not doing optimistic deletes
          }
        },
        onSuccess: () => {
          // If successful, update the reducer state
          dispatch(deleteThreadAction(threadableId, threadId));

          // Invalidate relevant caches
          mutate(threadsUrl);
          mutate(threadUrl);
        },
      });
    },
    [dispatch, currentThreadableId]
  );

  const deleteMessage = useCallback(
    (threadId: string, messageId: string) => {
      if (!threadId || !messageId) {
        console.error('Cannot delete message: missing threadId or messageId');
        return;
      }

      const threadableId = currentThreadableId;
      if (!threadableId) {
        console.error('Cannot delete message: missing threadableId');
        return;
      }

      // API endpoint to delete the message
      const threadsUrl = getThreadsUrl(threadableId);
      const messagesUrl = threadsUrl + `/${threadId}/messages`;
      const messageUrl = messagesUrl + `/${messageId}`;

      // Make the API call to delete the message
      apiRequest<'message.delete'>(messageUrl, mutate, 'DELETE', {
        setErrors: (errors) => {
          if (Object.keys(errors).length > 0) {
            console.error('Error deleting message:', errors);
            // Note: No optimistic update to revert since we're not doing optimistic deletes
          }
        },
        onSuccess: () => {
          // If successful, update the reducer state
          dispatch(deleteMessageAction(threadableId, threadId, messageId));

          // Invalidate relevant caches
          mutate(messagesUrl);
          mutate(threadsUrl);
        },
      });
    },
    [dispatch, currentThreadableId]
  );

  const markThreadAsRead = useCallback(
    (threadableId: string, threadId: string, messageId: string) => {
      if (!threadId) {
        console.error('Cannot mark thread as read: missing threadId');
        return;
      }
      if (!threadableId) {
        console.error('Cannot mark thread as read: missing threadableId');
        return;
      }

      if (!messageId) {
        console.error('Cannot mark thread as read: missing messageId');
        return;
      }

      dispatch(markThreadAsReadAction(threadableId, threadId, messageId));
      markThreadAsReadHook(threadableId, threadId, messageId);
    },
    [dispatch, markThreadAsReadHook]
  );

  const addReaction = useCallback(
    (threadId: string, messageId: string, emoji: string) => {
      if (!threadId || !messageId || !emoji) {
        console.error('Cannot add reaction: missing threadId, messageId, or emoji');
        return;
      }

      if (!user) {
        console.error('Cannot add reaction: user not authenticated');
        return;
      }

      const threadableId = currentThreadableId;
      if (!threadableId) {
        console.error('Cannot react to message: missing threadableId');
        return;
      }

      // Create a temporary ID for optimistic updates
      const tempId = `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

      // 1. Optimistically add the reaction to the UI
      dispatch(addReactionOptimisticAction(threadableId, threadId, messageId, tempId, emoji, user));

      // 2. Actually send the reaction to the API
      const threadsUrl = getThreadsUrl(threadableId);
      const messagesUrl = threadsUrl + `/${threadId}/messages`;
      const reactionsUrl = messagesUrl + `/${messageId}/reactions`;

      // necessary to avoid TypeScript complaining about this being potentially false.
      apiRequest<'message.react'>(reactionsUrl, mutate, 'POST', {
        setErrors: (errors) => {
          if (Object.keys(errors).length > 0) {
            console.error('Error sending message:', errors);
            dispatch(addReactionRevertAction(threadableId, threadId, messageId, tempId));
          }
        },
        payload: { content: emoji },
        onSuccess: (syncedMessage) => {
          // Handle success
          dispatch(
            addReactionConfirmedAction(
              threadableId,
              threadId,
              messageId,
              tempId,
              syncedMessage as Groupthink.MessageResource
            )
          );
          mutate(reactionsUrl);
          mutate(messagesUrl);
          mutate(threadsUrl);
        },
      });
    },
    [dispatch, user, currentThreadableId]
  );

  const removeReaction = useCallback(
    (threadId: string, messageId: string, emoji: string) => {
      if (!threadId || !messageId || !emoji) {
        console.error('Cannot remove reaction: missing threadId, messageId, or emoji');
        return;
      }

      const threadableId = currentThreadableId;
      if (!threadableId) {
        console.error('Cannot react to message: missing threadableId');
        return;
      }

      // 1. Optimistically remove the reaction from the UI
      dispatch(removeReactionOptimisticAction(threadableId, threadId, messageId, emoji, user));

      // 2. Actually send the reaction to the API
      const threadsUrl = getThreadsUrl(threadableId);
      const messagesUrl = threadsUrl + `/${threadId}/messages`;
      const reactionsUrl = messagesUrl + `/${messageId}/reactions`;

      // necessary to avoid TypeScript complaining about this being potentially false.
      apiRequest<'message.react'>(reactionsUrl, mutate, 'DELETE', {
        setErrors: (errors) => {
          if (Object.keys(errors).length > 0) {
            console.error('Error sending message:', errors);
            dispatch(removeReactionRevertAction(threadableId, threadId, messageId, emoji, user));
          }
        },
        payload: { content: emoji },
        onSuccess: (syncedMessage) => {
          // Handle success
          dispatch(removeReactionConfirmedAction(threadableId, threadId, messageId));
          mutate(reactionsUrl);
          mutate(messagesUrl);
          mutate(threadsUrl);
        },
      });
    },
    [dispatch, state.messages, currentThreadableId, user]
  );

  const editMessage = useCallback(
    async (threadId: string, messageId: string, content: string) => {
      if (!threadId || !messageId || !content) {
        console.error('Cannot edit message: missing threadId, messageId, or content');
        return;
      }

      const threadableId = currentThreadableId;
      if (!threadableId) {
        console.error('Cannot edit message: missing threadableId');
        return;
      }

      // API endpoint to edit the message
      const threadsUrl = getThreadsUrl(threadableId);
      const messagesUrl = threadsUrl + `/${threadId}/messages`;
      const messageUrl = messagesUrl + `/${messageId}`;

      // Make the API call to update the message
      await apiRequest<'message.update'>(messageUrl, mutate, 'PUT', {
        setErrors: (errors) => {
          if (Object.keys(errors).length > 0) {
            console.error('Error editing message:', errors);
            // No optimistic update to revert here
            throw new Error('Error editing message');
          }
        },
        payload: { content },
        onSuccess: (updatedMessage) => {
          // If successful, update the reducer state
          dispatch(
            editMessageAction(
              threadableId,
              threadId,
              messageId,
              content,
              updatedMessage as Groupthink.MessageResource
            )
          );

          // Invalidate relevant caches
          mutate(messageUrl);
          mutate(messagesUrl);
          mutate(threadsUrl);
        },
      });
    },
    [dispatch, currentThreadableId]
  );

  // Memoize value with minimal dependencies
  const value = React.useMemo(
    () => ({
      state,
      dispatch,
      currentThreadableId,
      currentThreadId,
      mutateRooms,
      mutateTasks,
      mutateThreads,
      mutateMessages,
      setCurrentThreadable,
      deleteThreadable,
      leaveThreadable,
      fetchMoreThreads,
      setCurrentThread,
      markThreadAsRead,
      sendMessage,
      deleteMessage,
      deleteThread,
      editMessage,
      addReaction,
      removeReaction,
      selectors: {
        selectRooms,
        selectRoomsLoading,
        selectRoomsValidating,
        selectTasks,
        selectTasksLoading,
        selectTasksValidating,
        selectThreads,
        selectThreadsLoading,
        selectThreadsValidating,
        selectHasMorePages,
        selectMessages,
        selectMessagesLoading,
        selectMessagesValidating,
      },
    }),
    [
      state,
      dispatch,
      currentThreadableId,
      currentThreadId,
      mutateRooms,
      mutateTasks,
      mutateThreads,
      mutateMessages,
      setCurrentThreadable,
      deleteThreadable,
      fetchMoreThreads,
      setCurrentThread,
      markThreadAsRead,
      sendMessage,
      deleteMessage,
      deleteThread,
      editMessage,
      addReaction,
      removeReaction,
      leaveThreadable,
      selectRooms,
      selectRoomsLoading,
      selectRoomsValidating,
      selectTasks,
      selectTasksLoading,
      selectTasksValidating,
      selectThreads,
      selectThreadsLoading,
      selectThreadsValidating,
      selectHasMorePages,
      selectMessages,
      selectMessagesLoading,
      selectMessagesValidating,
    ]
  );

  // @ts-ignore
  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
};

export function useChat() {
  const context = React.useContext(ChatContext);
  if (context === undefined) {
    throw new Error('useChat must be used within a ChatContext');
  }
  return context;
}
